"use client";

import { showDevDetails } from "@/utils/helpers/demoMode";
import {
  isChunkLoadError,
  reloadOnceForChunkError,
} from "@/utils/helpers/errorBoundaryRecovery";
import * as Sentry from "@sentry/nextjs";
// Router hooks removed - using window APIs directly to avoid router mount errors
import { type CSSProperties, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheck,
  FiChevronDown,
  FiChevronRight,
  FiCopy,
  FiRefreshCw,
} from "react-icons/fi";

// Enhanced DOM diagnostic function for DOM reconciliation errors
const captureDetailedDOMDiagnostics = (error: Error) => {
  if (typeof window === "undefined") return null;

  try {
    const active = document.activeElement as HTMLElement | null;

    // Capture ALL Radix Popovers with detailed info
    const popoverTriggers = Array.from(
      document.querySelectorAll("[data-radix-popover-trigger]"),
    ).map((el) => {
      const trigger = el as HTMLElement;
      const isOpen = trigger.getAttribute("aria-expanded") === "true";
      const popoverId = trigger.getAttribute("aria-controls");
      const popoverContent = popoverId
        ? document.querySelector(`[id="${popoverId}"]`)
        : null;

      return {
        tagName: trigger.tagName,
        id: trigger.id,
        className: trigger.className,
        isOpen,
        popoverId,
        hasContent: !!popoverContent,
        contentClasses: popoverContent?.className || null,
        contentParent: popoverContent?.parentElement?.tagName || null,
        triggerHTML: trigger.outerHTML.slice(0, 300),
      };
    });

    // Capture ALL Radix Selects with detailed info
    const selectTriggers = Array.from(
      document.querySelectorAll("[data-radix-select-trigger]"),
    ).map((el) => {
      const trigger = el as HTMLElement;
      const isOpen = trigger.getAttribute("aria-expanded") === "true";
      const selectId = trigger.getAttribute("aria-controls");
      const selectContent = selectId
        ? document.querySelector(`[id="${selectId}"]`)
        : null;

      return {
        tagName: trigger.tagName,
        id: trigger.id,
        className: trigger.className,
        isOpen,
        selectId,
        hasContent: !!selectContent,
        contentClasses: selectContent?.className || null,
        contentParent: selectContent?.parentElement?.tagName || null,
        triggerHTML: trigger.outerHTML.slice(0, 300),
      };
    });

    // Capture ALL portals (Radix and custom) with enhanced diagnostics
    const allPortals = Array.from(
      document.querySelectorAll(
        '[data-radix-portal], [data-radix-popper-content], [role="dialog"], [role="menu"], [role="listbox"]',
      ),
    ).map((el, idx) => {
      const portal = el as HTMLElement;
      const parent = portal.parentElement;
      const isInBody = parent === document.body;
      const hasParent = !!parent;
      const parentHasParent = !!parent?.parentElement;

      return {
        index: idx,
        tagName: portal.tagName,
        id: portal.id,
        className: portal.className,
        role: portal.getAttribute("role"),
        parentTag: parent?.tagName || null,
        parentId: parent?.id || null,
        parentClasses: parent?.className || null,
        isVisible: portal.offsetParent !== null,
        isInBody,
        hasParent,
        parentHasParent,
        zIndex: window.getComputedStyle(portal).zIndex,
        childCount: portal.childElementCount,
        // Check if portal is in the process of being removed
        isConnected: portal.isConnected,
        // Get all data attributes for identification
        dataAttributes: Array.from(portal.attributes)
          .filter((attr) => attr.name.startsWith("data-"))
          .reduce(
            (acc, attr) => {
              acc[attr.name] = attr.value;
              return acc;
            },
            {} as Record<string, string>,
          ),
        html: portal.outerHTML.slice(0, 500),
      };
    });

    // Capture body structure
    const bodyChildren = Array.from(document.body.children).map(
      (child, idx) => {
        const el = child as HTMLElement;
        return {
          index: idx,
          tagName: el.tagName,
          id: el.id,
          className: el.className,
          hasPortals: el.querySelectorAll(
            "[data-radix-portal], [data-radix-popper-content]",
          ).length,
          childCount: el.childElementCount,
        };
      },
    );

    // Try to extract React component info from error stack
    const reactComponentMatch = error.stack?.match(/at\s+(\w+)\s*\(/g) || [];
    const reactComponents = reactComponentMatch
      .map((match) => match.replace(/at\s+/, "").replace(/\(/, ""))
      .filter(
        (name) =>
          name &&
          !name.startsWith("a") &&
          !name.startsWith("_") &&
          name.length > 2 &&
          !/^[a-z]$/.test(name),
      )
      .slice(0, 10);

    // Capture recent breadcrumbs for context
    let recentBreadcrumbs: Sentry.Breadcrumb[] = [];
    try {
      const scope = Sentry.getCurrentScope();
      if (scope && "breadcrumbs" in scope) {
        const breadcrumbs = (scope as { breadcrumbs?: Sentry.Breadcrumb[] })
          .breadcrumbs;
        recentBreadcrumbs = Array.isArray(breadcrumbs)
          ? breadcrumbs.slice(-10)
          : [];
      }
    } catch {
      // Breadcrumbs may not be available
    }

    return {
      timestamp: new Date().toISOString(),
      activeElement: {
        tagName: active?.tagName || null,
        id: active?.id || null,
        className: active?.className || null,
        tag: active?.tagName || null,
      },
      popovers: {
        total: popoverTriggers.length,
        open: popoverTriggers.filter((p) => p.isOpen).length,
        details: popoverTriggers,
      },
      selects: {
        total: selectTriggers.length,
        open: selectTriggers.filter((s) => s.isOpen).length,
        details: selectTriggers,
      },
      portals: {
        total: allPortals.length,
        visible: allPortals.filter((p) => p.isVisible).length,
        connected: allPortals.filter((p) => p.isConnected).length,
        inBody: allPortals.filter((p) => p.isInBody).length,
        disconnected: allPortals.filter((p) => !p.isConnected).length,
        details: allPortals,
        // Identify potentially problematic portals
        problematic: allPortals.filter(
          (p) =>
            !p.isConnected ||
            (!p.hasParent && !p.isInBody) ||
            (p.parentTag && p.parentTag === "BODY" && !p.isInBody),
        ),
      },
      bodyStructure: {
        childCount: document.body.childElementCount,
        children: bodyChildren,
      },
      reactStack: {
        components: reactComponents,
        fullStack: error.stack?.split("\n").slice(0, 50) || [],
        // Try to identify the specific operation causing the error
        insertBeforeMatches: error.stack?.match(/insertBefore[^\n]*/g) || [],
        portalMatches: error.stack?.match(/[Pp]ortal[^\n]*/g) || [],
        radixMatches: error.stack?.match(/[Rr]adix[^\n]*/g) || [],
      },
      recentBreadcrumbs: recentBreadcrumbs.map((b) => ({
        message: b.message,
        category: b.category,
        level: b.level,
        data: b.data,
        timestamp: b.timestamp,
      })),
      errorMessage: error.message,
      errorName: error.name,
    };
  } catch (diagError) {
    return {
      diagnosticError:
        diagError instanceof Error ? diagError.message : String(diagError),
    };
  }
};

// Enhanced error context capture
const captureErrorWithContext = (error: Error) => {
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const userAgent = navigator.userAgent;
  const timestamp = new Date().toISOString();

  // Extract meaningful context from the error
  const errorContext = {
    // Router context
    pathname,
    searchParams: Object.fromEntries(searchParams.entries()),
    hash: window.location.hash,

    // Browser context
    userAgent,
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,

    // Error context
    errorMessage: error.message,
    errorName: error.name,
    errorStack: error.stack,
    timestamp,

    // App-specific context
    isCandidatePage: pathname.includes("/candidates/"),
    isTokenPage: pathname.includes("/[token]"),
    token: pathname.includes("/candidates/")
      ? pathname.split("/candidates/")[1]
      : null,

    // Performance context
    performanceTiming: {
      navigationStart: performance.timing?.navigationStart,
      loadEventEnd: performance.timing?.loadEventEnd,
      domContentLoaded: performance.timing?.domContentLoadedEventEnd,
    },

    // Memory context
    memoryInfo: ((): {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    } | null => {
      type PerformanceWithMemory = Performance & {
        memory?: {
          usedJSHeapSize: number;
          totalJSHeapSize: number;
          jsHeapSizeLimit: number;
        };
      };
      const perf = performance as PerformanceWithMemory;
      return perf.memory
        ? {
            usedJSHeapSize: perf.memory.usedJSHeapSize,
            totalJSHeapSize: perf.memory.totalJSHeapSize,
            jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
          }
        : null;
    })(),
  };

  // Categorize error types
  let errorCategory = "unknown";
  let errorSeverity = "error";

  const isDOMReconciliationError =
    error?.name === "NotFoundError" ||
    /insertBefore|removeChild|appendChild/.test(error?.message || "");

  // Enhanced DOM diagnostics for DOM reconciliation errors
  const domDiagnostics = isDOMReconciliationError
    ? captureDetailedDOMDiagnostics(error)
    : null;

  if (isDOMReconciliationError) {
    errorCategory = "dom_reconciliation";
    errorSeverity = "warning";
  } else if (
    error.message.includes("invariant expected app router to be mounted")
  ) {
    errorCategory = "router_mount_error";
    errorSeverity = "error";
  } else if (error.message.includes("Network request failed")) {
    errorCategory = "network_error";
    errorSeverity = "warning";
  } else if (error.message.includes("ChunkLoadError")) {
    errorCategory = "chunk_load_error";
    errorSeverity = "error";
  } else if (error.message.includes("Loading chunk")) {
    errorCategory = "chunk_loading_error";
    errorSeverity = "error";
  } else if (error instanceof TypeError) {
    errorCategory = "type_error";
    errorSeverity = "error";
  } else if (error instanceof ReferenceError) {
    errorCategory = "reference_error";
    errorSeverity = "error";
  } else if (
    error.message.includes("Rendered more hooks") ||
    error.message.includes("Minified React error #310")
  ) {
    errorCategory = "react_hooks_error";
    errorSeverity = "error";
  }

  // Verificar que Sentry está disponible y habilitado
  const isProduction = process.env.NODE_ENV === "production";

  // Verificar que Sentry está disponible, habilitado y tiene el método captureException
  const isSentryAvailable =
    typeof Sentry !== "undefined" &&
    Sentry.captureException &&
    typeof Sentry.captureException === "function";

  // Verificar si Sentry está habilitado
  let isSentryEnabled = false;
  try {
    const client = Sentry.getClient();
    isSentryEnabled = client ? client.getOptions()?.enabled !== false : false;
  } catch {
    // Si no podemos verificar, asumimos que está habilitado si está disponible
    isSentryEnabled = isSentryAvailable;
  }

  if (isSentryAvailable && (isProduction ? isSentryEnabled : true)) {
    try {
      // Force error level for all exceptions to ensure they're sent
      const severityLevel: Sentry.SeverityLevel = "error";

      // Capture with enhanced context
      // Try to extract component name from error stack or component stack
      let componentName = "GlobalError";
      try {
        // Look for component names in the stack trace
        const stack = error.stack || "";
        const componentMatch = stack.match(
          /(PeopleGeneral|PeopleEducation|PeopleExperience|PeopleInterview|PeopleAvailability|PeopleLog|PeopleSendInfo|PeopleFinancials|PeopleFiles|PeopleOther|PeopleHistory|Communications|FilterSidebar|PeopleList)/i,
        );
        if (componentMatch) {
          componentName = componentMatch[1];
        }
      } catch {
        // Ignore errors in error extraction
      }

      const eventId = Sentry.captureException(error, {
        tags: {
          errorCategory,
          errorSeverity,
          component: componentName,
          page: pathname,
          isCandidatePage: errorContext.isCandidatePage,
          hasToken: !!errorContext.token,
          source: "global-error-boundary",
          isHooksError:
            errorCategory === "react_hooks_error" ? "true" : "false",
          // Add diagnostic tags for DOM errors
          ...(domDiagnostics
            ? {
                openPopoversCount:
                  domDiagnostics.popovers?.open.toString() || "0",
                openSelectsCount:
                  domDiagnostics.selects?.open.toString() || "0",
                portalsCount: domDiagnostics.portals?.total.toString() || "0",
                visiblePortalsCount:
                  domDiagnostics.portals?.visible.toString() || "0",
                connectedPortalsCount:
                  domDiagnostics.portals?.connected?.toString() || "0",
                disconnectedPortalsCount:
                  domDiagnostics.portals?.disconnected?.toString() || "0",
                problematicPortalsCount:
                  domDiagnostics.portals?.problematic?.length.toString() || "0",
                // Identify which specific components have open portals
                openSelectsDetails:
                  domDiagnostics.selects?.details
                    ?.filter((s) => s.isOpen)
                    .map(
                      (s) =>
                        `${s.tagName}#${s.id || "no-id"}.${s.className?.split(" ")[0] || "no-class"}`,
                    )
                    .join(", ") || "none",
                openPopoversDetails:
                  domDiagnostics.popovers?.details
                    ?.filter((p) => p.isOpen)
                    .map(
                      (p) =>
                        `${p.tagName}#${p.id || "no-id"}.${p.className?.split(" ")[0] || "no-class"}`,
                    )
                    .join(", ") || "none",
                // Identify problematic portals (disconnected or orphaned)
                problematicPortalsDetails:
                  domDiagnostics.portals?.problematic
                    ?.map(
                      (p) =>
                        `${p.tagName}${p.id ? `#${p.id}` : ""}${p.className ? `.${p.className.split(" ")[0]}` : ""}${!p.isConnected ? "[DISCONNECTED]" : ""}`,
                    )
                    .join(", ") || "none",
              }
            : {}),
        },
        extra: {
          ...errorContext,
          sentryEnabled: isSentryEnabled,
          sentryAvailable: isSentryAvailable,
          extractedComponent: componentName,
          // Add React component stack if available
          reactComponentStack:
            (error as Error & { componentStack?: string }).componentStack ||
            null,
          // CRITICAL: Include detailed diagnostics for DOM reconciliation errors
          ...(domDiagnostics
            ? {
                domDiagnostics, // Full detailed diagnostics
                // Summary for quick identification
                summary: {
                  errorMessage: error.message,
                  component: componentName,
                  openSelects: domDiagnostics.selects?.open || 0,
                  openPopovers: domDiagnostics.popovers?.open || 0,
                  totalPortals: domDiagnostics.portals?.total || 0,
                  visiblePortals: domDiagnostics.portals?.visible || 0,
                  connectedPortals: domDiagnostics.portals?.connected || 0,
                  disconnectedPortals:
                    domDiagnostics.portals?.disconnected || 0,
                  problematicPortals:
                    domDiagnostics.portals?.problematic?.length || 0,
                  problematicPortalsDetails:
                    domDiagnostics.portals?.problematic || [],
                  activeElement: domDiagnostics.activeElement,
                  reactComponents: domDiagnostics.reactStack?.components || [],
                  reactStackMatches: {
                    insertBefore:
                      domDiagnostics.reactStack?.insertBeforeMatches || [],
                    portal: domDiagnostics.reactStack?.portalMatches || [],
                    radix: domDiagnostics.reactStack?.radixMatches || [],
                  },
                },
              }
            : {}),
        },
        level: severityLevel, // Always use error level to ensure it's sent
        fingerprint: [
          errorCategory,
          pathname,
          error.message,
          componentName,
          // Include portal state in fingerprint for better grouping
          ...(domDiagnostics
            ? [
                `selects:${domDiagnostics.selects?.open || 0}`,
                `popovers:${domDiagnostics.popovers?.open || 0}`,
              ]
            : []),
        ],
      });

      // Add breadcrumb for context
      Sentry.addBreadcrumb({
        message: `Global error occurred on ${pathname}`,
        level: "error",
        category: "error",
        data: {
          errorMessage: error.message,
          errorCategory,
          pathname,
          timestamp,
        },
      });

      if (isProduction) {
        console.log(
          `[GlobalError] Error reported to Sentry. Event ID: ${eventId || "pending"}`,
        );
        console.log(
          `[GlobalError] Sentry status - Available: ${isSentryAvailable}, Enabled: ${isSentryEnabled}`,
        );
      } else {
        console.log(
          `[GlobalError] Error logged (Sentry disabled in development). Event ID: ${
            eventId || "N/A"
          }`,
        );
      }
    } catch (captureError) {
      // Si falla la captura, loguear el error
      console.error(
        "[GlobalError] Failed to capture error to Sentry:",
        captureError,
      );
      console.error("[GlobalError] Original error:", error);
    }
  } else {
    console.error("[GlobalError] Sentry not available or not enabled", {
      isSentryAvailable,
      isSentryEnabled,
      isProduction,
    });
  }
};

// Errors already reported by this boundary. The boundary can render more than
// once for the same error, and reporting happens during render (see below).
const reportedErrors = new WeakSet<object>();

// Reports the error once and never throws. It runs during render instead of in
// a useEffect: if anything in this boundary throws, React never commits it, the
// effect never runs, and Next falls back to its built-in "Application error"
// page without anything reaching Sentry.
const reportGlobalErrorOnce = (error: Error & { digest?: string }) => {
  try {
    if (reportedErrors.has(error)) return;
    reportedErrors.add(error);

    const isDOMReconciliationError =
      error?.name === "NotFoundError" ||
      /insertBefore|removeChild|appendChild/.test(error?.message || "");

    Sentry.addBreadcrumb({
      message: "Global error boundary triggered",
      level: isDOMReconciliationError ? "warning" : "error",
      category: "error",
      data: {
        pathname: window.location.pathname,
        searchParams: Object.fromEntries(
          new URLSearchParams(window.location.search).entries(),
        ),
        errorMessage: error.message,
        errorName: error.name,
        isDOMReconciliationError,
      },
    });

    // Normalize to an Error instance for Sentry
    const normalizedError =
      error instanceof Error
        ? error
        : new Error(
            String(
              (error as unknown as { message?: string })?.message ||
                String(error),
            ),
          );

    captureErrorWithContext(normalizedError);
  } catch (reportError) {
    // The detailed capture failed; send the bare error so it is not lost.
    try {
      Sentry.captureException(error, {
        tags: { source: "global-error-boundary", reportFallback: "true" },
        extra: {
          reportError:
            reportError instanceof Error
              ? reportError.message
              : String(reportError),
        },
      });
    } catch {
      // Nothing else to do.
    }
  }
};

// Inline styles: this boundary replaces the root layout, so the global
// stylesheet (and Tailwind) is not guaranteed to be loaded when it renders.
const styles = {
  body: {
    margin: 0,
    backgroundColor: "#f9fafb",
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  wrapper: {
    minHeight: "100dvh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    boxSizing: "border-box",
  },
  card: {
    maxWidth: "448px",
    width: "100%",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    padding: "32px",
    textAlign: "center",
    boxSizing: "border-box",
  },
  icon: { color: "#ef4444", margin: "0 auto 16px", display: "block" },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 8px",
  },
  text: { color: "#4b5563", margin: "0 0 24px", lineHeight: 1.5 },
  primaryButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "10px 16px",
    marginBottom: "12px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
  },
  secondaryButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "10px 16px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    color: "#111827",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
  },
  details: { marginTop: "24px", textAlign: "left" },
  summary: {
    cursor: "pointer",
    fontSize: "14px",
    color: "#6b7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    listStyle: "none",
  },
  summaryLabel: { display: "flex", alignItems: "center", gap: "8px" },
  copyButton: {
    border: "none",
    background: "transparent",
    padding: "4px",
    cursor: "pointer",
    color: "inherit",
  },
  detailsBox: {
    marginTop: "8px",
    padding: "12px",
    backgroundColor: "#f3f4f6",
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "monospace",
    color: "#1f2937",
    overflow: "auto",
    maxHeight: "240px",
  },
  pre: { marginTop: "4px", whiteSpace: "pre-wrap", fontSize: "12px" },
} satisfies Record<string, CSSProperties>;

function GlobalErrorContent({ error }: { error: Error & { digest?: string } }) {
  if (typeof window !== "undefined") {
    reportGlobalErrorOnce(error);
  }

  const [copied, setCopied] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Decided once per mount: a re-render must not consume the one-reload guard
  // and show the error UI while the scheduled reload is still pending.
  const [reloadingForChunkError] = useState(
    () => isChunkLoadError(error) && reloadOnceForChunkError(),
  );

  const handleGoBack = () => {
    // Use window.history directly to avoid router mount errors
    try {
      window.history.back();
    } catch (error) {
      console.warn("history.back() failed:", error);
      // Fallback: reload the page
      window.location.reload();
    }
  };

  const handleRefresh = () => {
    try {
      Sentry.addBreadcrumb({
        message: "Global error: user clicked refresh",
        category: "user",
        level: "info",
      });
    } catch {}
    window.location.reload();
  };

  const handleCopyError = async () => {
    try {
      const errorContent = {
        component: "GlobalError",
        timestamp: new Date().toISOString(),
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          digest: error.digest,
        },
        url: typeof window !== "undefined" ? window.location.href : "",
        pathname: typeof window !== "undefined" ? window.location.pathname : "",
      };

      const errorText = JSON.stringify(errorContent, null, 2);
      await navigator.clipboard.writeText(errorText);

      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy error message:", err);
    }
  };

  // Determine if this is a DOM reconciliation error (used below for silent recovery)
  const isDOMReconciliationError =
    error?.name === "NotFoundError" ||
    /insertBefore|removeChild|appendChild/.test(error?.message || "");

  // For DOM reconciliation errors, auto-recover silently without showing error UI
  if (isDOMReconciliationError) {
    if (typeof window !== "undefined") {
      setTimeout(() => {
        try {
          window.history.back();
        } catch {
          window.location.reload();
        }
      }, 0);
    }
    return (
      <html lang="es">
        <body style={styles.body}>
          <div style={{ minHeight: "100dvh" }} />
        </body>
      </html>
    );
  }

  // Chunk load errors: a client open across a deploy asks for a chunk of the
  // previous build. A hard reload fetches the current build, so it is attempted
  // once per session (see reloadOnceForChunkError); if the error comes back, the
  // normal error UI below is shown instead of reloading forever.
  if (reloadingForChunkError) {
    return (
      <html lang="es">
        <body style={styles.body}>
          <div style={{ minHeight: "100dvh" }} />
        </body>
      </html>
    );
  }

  return (
    <html lang="es">
      <body style={styles.body}>
        <div style={styles.wrapper}>
          <div style={styles.card}>
            <FiAlertCircle style={styles.icon} size={48} />
            <h1 style={styles.title}>Algo salió mal</h1>
            <p style={styles.text}>
              Hemos encontrado un error inesperado. No te preocupes, nuestro
              equipo ha sido notificado.
            </p>

            <button
              type="button"
              onClick={handleRefresh}
              style={styles.primaryButton}
            >
              <FiRefreshCw size={16} />
              Intentar de nuevo
            </button>

            <button
              type="button"
              onClick={handleGoBack}
              style={styles.secondaryButton}
            >
              <FiArrowLeft size={16} />
              Volver atrás
            </button>

            {showDevDetails() && (
              <details
                style={styles.details}
                onToggle={(e) => {
                  setDetailsOpen(e.currentTarget.open);
                }}
              >
                <summary style={styles.summary}>
                  <span style={styles.summaryLabel}>
                    {detailsOpen ? (
                      <FiChevronDown size={14} />
                    ) : (
                      <FiChevronRight size={14} />
                    )}
                    Detalles del error (solo desarrollo, global-error.tsx)
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyError();
                    }}
                    style={styles.copyButton}
                    title={copied ? "Copiado" : "Copiar error"}
                  >
                    {copied ? (
                      <FiCheck size={14} color="#16a34a" />
                    ) : (
                      <FiCopy size={14} />
                    )}
                  </button>
                </summary>
                <div style={styles.detailsBox}>
                  <div style={{ marginBottom: "8px" }}>
                    <strong>Error:</strong> {error.message}
                  </div>
                  {error.stack && (
                    <div>
                      <strong>Stack:</strong>
                      <pre style={styles.pre}>{error.stack}</pre>
                    </div>
                  )}
                  {error.digest && (
                    <div style={{ marginTop: "8px" }}>
                      <strong>Digest:</strong> {error.digest}
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}

// No Suspense wrapper: nothing here suspends, and a Suspense boundary above
// <html> would render its fallback outside of any document.
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  return <GlobalErrorContent error={error} />;
}
